/**
 * React Query Hooks for Rule Management
 *
 * Provides hooks for managing compliance rules with caching, automatic refetching,
 * and optimistic updates following the existing pattern from useSubscriptions.ts
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { ruleManagementApi } from "../api/rules";
import type {
  Rule,
  RuleFilters,
  CreateRuleDto,
  UpdateRuleDto,
  ChangeRuleStatusDto,
  PaginatedRuleResponse,
  RuleHistoryEntry,
  RuleCategoryResponse,
  RuleSet,
  ChapterRuleOptionDto,
  RuleTaggedDocumentDto,
  PageResponse,
  TaggedDocumentsParams,
} from "../api/rules";
import type { ApiResponse } from "../api/client";
import type { RuleStatus } from "../api/rules";
import { queryKeys } from "../api/queryKeys";
import {
  invalidateRules,
  invalidateRuleAndList,
} from "../api/queryInvalidation";
import { useTenantId } from "./useTenantId";

// ================================
// Backward-compatible alias (deprecated — use queryKeys.rules instead)
// ================================

/** @deprecated Use `queryKeys.rules` from `../api/queryKeys` instead */
export const ruleKeys = queryKeys.rules;

// ================================
// Query Hooks (Read Operations)
// ================================

/**
 * List Rules with Pagination and Filtering
 *
 * @param filters - Optional filters for search, category, status, pagination, etc.
 * @param options - Optional query options (e.g., enabled)
 * @returns Paginated list of rules
 *
 * @example
 * const { data, isLoading, error } = useRules({
 *   status: RuleStatus.ACTIVE,
 *   page: 0,
 *   size: 20
 * });
 *
 * // Conditional fetching
 * const { data } = useRules({ size: 1000 }, { enabled: dialogOpen });
 */
export const useRules = (
  filters?: RuleFilters,
  options?: { enabled?: boolean; taggedDocParams?: TaggedDocumentsParams },
): UseQueryResult<ApiResponse<PaginatedRuleResponse>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.list(tenantId, filters, options?.taggedDocParams),
    queryFn: () =>
      ruleManagementApi.listRules(filters, options?.taggedDocParams),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Get Single Rule by ID
 *
 * @param id - Rule ID
 * @param enabled - Whether to enable the query (default: true if id > 0)
 * @returns Full rule details including history, parent/child rules
 *
 * @example
 * const { data, isLoading } = useRule(1);
 * if (data?.success) {
 *   const rule = data.data;
 *   console.log(rule.title, rule.ruleStatus);
 * }
 */
export const useRule = (
  id: number,
  enabled: boolean = true,
): UseQueryResult<ApiResponse<Rule>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.detail(tenantId, id),
    queryFn: () => ruleManagementApi.getRuleById(id),
    enabled: enabled && id > 0,
    staleTime: 1 * 60 * 1000, // 1 minute - detail pages need fresher data
  });
};

/**
 * Get Rule Categories for Dropdowns/Filters
 *
 * @returns List of all rule categories
 *
 * @example
 * const { data } = useRuleCategories();
 * const categories = data?.data?.categories || [];
 */
export const useRuleCategories = (): UseQueryResult<
  ApiResponse<RuleCategoryResponse>
> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.ruleCategories.all(tenantId),
    queryFn: () => ruleManagementApi.getRuleCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes - categories rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });
};

/**
 * Get unique RuleSets extracted from rules data.
 * Fetches a large page of rules and deduplicates by ruleSet.id.
 *
 * @returns Array of unique RuleSet objects (sorted by name)
 */
export const useRuleSets = (options?: {
  enabled?: boolean;
}): UseQueryResult<RuleSet[]> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.ruleSets(tenantId),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await ruleManagementApi.listRules({
        size: 1000,
        page: 1,
      });
      const rules = response.data?.content ?? [];
      const map = new Map<number, RuleSet>();
      for (const rule of rules) {
        if (rule.ruleSet?.id != null && !map.has(rule.ruleSet.id)) {
          map.set(rule.ruleSet.id, rule.ruleSet);
        }
      }
      return Array.from(map.values()).sort((a, b) =>
        (a.displayName || a.name).localeCompare(b.displayName || b.name),
      );
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - rulesets rarely change
    gcTime: 30 * 60 * 1000,
  });
};

export const useChapterRules = (
  rulesetId: number | null,
): UseQueryResult<ApiResponse<ChapterRuleOptionDto[]>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.chapterRules(tenantId, rulesetId ?? 0),
    queryFn: () => ruleManagementApi.getChapterRules(rulesetId!),
    enabled: rulesetId != null,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Get Chapter List for Filter Dropdown
 * ALH-267: Returns distinct chapter names from the backend
 *
 * @param tenantId - Optional tenant filter
 * @returns Array of chapter name strings
 */
export const useChapterList = (
  tenantId?: number | null,
): UseQueryResult<ApiResponse<string[]>> => {
  const effectiveTenantId = useTenantId();
  const tid = tenantId ?? effectiveTenantId;

  return useQuery({
    queryKey: queryKeys.rules.chapters(tid),
    queryFn: () => ruleManagementApi.getChapterList(tenantId),
    staleTime: 5 * 60 * 1000, // 5 minutes - chapters rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });
};

/**
 * Get Child Rules for a Parent Rule
 *
 * @param parentId - Parent rule ID
 * @param enabled - Whether to enable the query
 * @returns List of child rules
 */
export const useChildRules = (
  parentId: number,
  enabled: boolean = true,
): UseQueryResult<ApiResponse<Rule[]>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.children(tenantId, parentId),
    queryFn: () => ruleManagementApi.getChildRules(parentId),
    enabled: enabled && parentId > 0,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get Rule Version History
 *
 * @param ruleId - Rule ID
 * @param enabled - Whether to enable the query
 * @returns List of history entries
 */
export const useRuleHistory = (
  ruleId: number,
  enabled: boolean = true,
): UseQueryResult<ApiResponse<RuleHistoryEntry[]>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.history(tenantId, ruleId),
    queryFn: () => ruleManagementApi.getRuleHistory(ruleId),
    enabled: enabled && ruleId > 0,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Get Rule Statistics
 *
 * @returns Statistics about rules (count by status, priority, etc.)
 */
export const useRuleStatistics = (): UseQueryResult<
  ApiResponse<{
    totalRules: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    bySeverity: Record<string, number>;
    byCategory: Record<string, number>;
  }>
> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.statistics(tenantId),
    queryFn: () => ruleManagementApi.getRuleStatistics(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Search Rules by Query
 *
 * @param query - Search query
 * @param limit - Max results
 * @param enabled - Whether to enable the query (default: query has at least 2 chars)
 * @returns List of matching rules
 */
export const useSearchRules = (
  query: string,
  limit: number = 20,
  enabled?: boolean,
): UseQueryResult<ApiResponse<Rule[]>> => {
  const tenantId = useTenantId();
  const shouldEnable = enabled !== undefined ? enabled : query.length >= 2;

  return useQuery({
    queryKey: queryKeys.rules.search(tenantId, query),
    queryFn: () => ruleManagementApi.searchRules(query, limit),
    enabled: shouldEnable,
    staleTime: 1 * 60 * 1000,
  });
};

// ================================
// Mutation Hooks (Write Operations)
// ================================

/**
 * Create New Rule
 *
 * @returns Mutation for creating a rule
 *
 * @example
 * const { mutate, isPending } = useCreateRule();
 *
 * mutate({
 *   title: "GDPR Data Retention",
 *   description: "...",
 *   ruleText: "...",
 *   categoryId: 5,
 *   priority: Priority.HIGH,
 *   severity: Severity.CRITICAL,
 *   scope: RuleScope.GLOBAL,
 *   effectiveFrom: '2025-01-01',
 *   tenantId: 1,
 *   createdBy: 3
 * }, {
 *   onSuccess: (response) => {
 *     console.log('Created rule:', response.data.id);
 *   }
 * });
 */
export const useCreateRule = (): UseMutationResult<
  ApiResponse<Rule>,
  Error,
  CreateRuleDto
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (data: CreateRuleDto) => ruleManagementApi.createRule(data),
    onSuccess: () => {
      invalidateRules(queryClient, tenantId);
    },
  });
};

/**
 * Update Existing Rule
 *
 * @returns Mutation for updating a rule
 *
 * @example
 * const { mutate } = useUpdateRule();
 *
 * mutate({
 *   id: 1,
 *   data: {
 *     title: "Updated Title",
 *     priority: Priority.CRITICAL
 *   }
 * });
 */
export const useUpdateRule = (): UseMutationResult<
  ApiResponse<Rule>,
  Error,
  { id: number; data: UpdateRuleDto }
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ id, data }) => ruleManagementApi.updateRule(id, data),
    onSuccess: (_response, variables) => {
      invalidateRuleAndList(queryClient, tenantId, variables.id);
    },
  });
};

/**
 * Delete (Archive) Rule
 *
 * @returns Mutation for deleting a rule
 *
 * @example
 * const { mutate, isPending } = useDeleteRule();
 *
 * mutate(1, {
 *   onSuccess: () => {
 *     toast.success('Rule archived successfully');
 *   }
 * });
 */
export const useDeleteRule = (): UseMutationResult<
  ApiResponse<{ message: string }>,
  Error,
  number
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (id: number) => ruleManagementApi.deleteRule(id),
    onSuccess: (_, ruleId) => {
      invalidateRuleAndList(queryClient, tenantId, ruleId);
    },
  });
};

/**
 * Change Rule Status
 *
 * Uses PATCH /api/rule-management/rules/{id}/status
 * with numeric lifecycle status and optional tenantId, matching Swagger.
 *
 * @returns Mutation for changing rule status
 *
 * @example
 * const { mutate } = useChangeRuleStatus();
 *
 * mutate({
 *   id: 1,
 *   status: GenericStatus.Active,
 *   tenantId: 42,
 * });
 */
export const useChangeRuleStatus = (): UseMutationResult<
  ApiResponse<Rule>,
  Error,
  { id: number; status: number; tenantId?: number | null }
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ id, status, tenantId: explicitTenantId }) => {
      const resolvedTenantId =
        explicitTenantId !== undefined
          ? explicitTenantId
          : (tenantId ?? undefined);
      return ruleManagementApi.changeRuleStatus(id, {
        status,
        tenantId: resolvedTenantId,
      } as ChangeRuleStatusDto);
    },
    onSuccess: (_response, variables) => {
      const effectiveTenantId =
        variables.tenantId !== undefined
          ? variables.tenantId
          : (tenantId ?? undefined);
      invalidateRuleAndList(queryClient, effectiveTenantId, variables.id);
    },
  });
};

/**
 * Duplicate Rule
 *
 * @returns Mutation for duplicating a rule
 *
 * @example
 * const { mutate } = useDuplicateRule();
 *
 * mutate({
 *   id: 1,
 *   newTitle: "Copy of Original Rule"
 * });
 */
export const useDuplicateRule = (): UseMutationResult<
  ApiResponse<Rule>,
  Error,
  { id: number; newTitle?: string }
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ id, newTitle }) =>
      ruleManagementApi.duplicateRule(id, newTitle),
    onSuccess: () => {
      invalidateRules(queryClient, tenantId);
    },
  });
};

/**
 * Bulk Update Rule Status
 *
 * @returns Mutation for bulk status update
 *
 * @example
 * const { mutate } = useBulkUpdateStatus();
 *
 * mutate({
 *   ruleIds: [1, 2, 3],
 *   newStatus: RuleStatus.ACTIVE,
 *   updatedBy: 3
 * });
 */
export const useBulkUpdateStatus = (): UseMutationResult<
  ApiResponse<{ message: string; updatedCount: number }>,
  Error,
  { ruleIds: number[]; newStatus: string; updatedBy: number }
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: ({ ruleIds, newStatus, updatedBy }) =>
      ruleManagementApi.bulkUpdateStatus(
        ruleIds,
        newStatus as RuleStatus,
        updatedBy,
      ),
    onSuccess: () => {
      invalidateRules(queryClient, tenantId);
    },
  });
};

/**
 * Bulk Delete Rules
 *
 * @returns Mutation for bulk deletion
 *
 * @example
 * const { mutate } = useBulkDeleteRules();
 *
 * mutate([1, 2, 3], {
 *   onSuccess: (response) => {
 *     console.log(`Deleted ${response.data.deletedCount} rules`);
 *   }
 * });
 */
export const useBulkDeleteRules = (): UseMutationResult<
  ApiResponse<{ message: string; deletedCount: number }>,
  Error,
  number[]
> => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return useMutation({
    mutationFn: (ruleIds: number[]) =>
      ruleManagementApi.bulkDeleteRules(ruleIds),
    onSuccess: () => {
      invalidateRules(queryClient, tenantId);
    },
  });
};

/**
 * Validate Rule Data
 *
 * @returns Mutation for rule validation
 *
 * @example
 * const { mutate, data } = useValidateRule();
 *
 * mutate(ruleData, {
 *   onSuccess: (response) => {
 *     if (response.data.valid) {
 *       console.log('Valid!');
 *     } else {
 *       console.error('Errors:', response.data.errors);
 *     }
 *   }
 * });
 */
export const useValidateRule = (): UseMutationResult<
  ApiResponse<{ valid: boolean; errors?: Record<string, string[]> }>,
  Error,
  Partial<CreateRuleDto | UpdateRuleDto>
> => {
  return useMutation({
    mutationFn: (data) => ruleManagementApi.validateRule(data),
  });
};

// ================================
// Utility Hooks
// ================================

/**
 * Prefetch Rule Detail
 *
 * Useful for preloading data on hover or before navigation
 *
 * @example
 * const prefetchRule = usePrefetchRule();
 *
 * <RuleCard
 *   onMouseEnter={() => prefetchRule(rule.id)}
 *   onClick={() => navigate(`/rules/${rule.id}`)}
 * />
 */
export const usePrefetchRule = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return (id: number) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.rules.detail(tenantId, id),
      queryFn: () => ruleManagementApi.getRuleById(id),
      staleTime: 1 * 60 * 1000,
    });
  };
};

/**
 * Invalidate All Rule Queries
 *
 * Useful for manual refresh or after bulk operations
 *
 * @example
 * const invalidateRulesHook = useInvalidateRules();
 * invalidateRulesHook(); // Refetches all active rule queries
 */
export const useInvalidateRules = () => {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();

  return () => {
    invalidateRules(queryClient, tenantId);
  };
};

// ================================
// Tree Hierarchy Hooks
// ================================

/**
 * Get Root Rules (for tree view)
 *
 * @param tenantId - Optional tenant ID
 * @returns Root-level rules (no parent)
 *
 * @example
 * const { data, isLoading } = useRootRules();
 * const rootRules = data?.data || [];
 */
export const useRootRules = (
  tenantId?: number,
  options?: { onlyRulesWithoutTaggedDocuments?: boolean },
): UseQueryResult<ApiResponse<Rule[]>> => {
  const effectiveTenantId = useTenantId();
  const tid = tenantId ?? effectiveTenantId;

  return useQuery({
    queryKey: queryKeys.rules.roots(tid, {
      onlyRulesWithoutTaggedDocuments: options?.onlyRulesWithoutTaggedDocuments,
    }),
    queryFn: () =>
      ruleManagementApi.getRootRules(tenantId, undefined, {
        onlyRulesWithoutTaggedDocuments:
          options?.onlyRulesWithoutTaggedDocuments,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Get Child Rules (for lazy loading tree nodes)
 *
 * @param parentId - Parent rule ID
 * @param enabled - Whether to enable the query
 * @returns Direct children of the parent rule
 *
 * @example
 * const { data, isLoading } = useChildRules(100, isExpanded);
 * const children = data?.data || [];
 */
export const useChildRulesForTree = (
  parentId: number,
  enabled: boolean = true,
  options?: { onlyRulesWithoutTaggedDocuments?: boolean },
): UseQueryResult<ApiResponse<Rule[]>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.children(tenantId, parentId, {
      onlyRulesWithoutTaggedDocuments: options?.onlyRulesWithoutTaggedDocuments,
    }),
    queryFn: () =>
      ruleManagementApi.getChildRules(parentId, {
        page: 1,
        pageSize: 100,
        onlyRulesWithoutTaggedDocuments:
          options?.onlyRulesWithoutTaggedDocuments,
      }),
    enabled: enabled && parentId > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Get Full Rule Tree (all levels loaded)
 *
 * ⚠️ WARNING: Use only for small rule sets (<500 rules)
 * For large datasets, use useRootRules + useChildRulesForTree
 *
 * @param tenantId - Optional tenant ID
 * @returns Complete tree with all children recursively loaded
 *
 * @example
 * const { data, isLoading } = useRuleTree();
 * const fullTree = data?.data || [];
 */
export const useRuleTree = (
  tenantId?: number,
): UseQueryResult<ApiResponse<Rule[]>> => {
  const effectiveTenantId = useTenantId();
  const tid = tenantId ?? effectiveTenantId;

  return useQuery({
    queryKey: queryKeys.rules.tree(tid),
    queryFn: () => ruleManagementApi.getRuleTree(tenantId),
    staleTime: 10 * 60 * 1000, // 10 minutes - full tree is expensive
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Get Tagged Documents for a Rule (full pagination)
 *
 * Uses GET /api/rule-management/rules/{id}/documents.
 * Prefer the embedded `taggedDocuments` on the rule detail for quick previews;
 * use this hook when the user opens the full Documents tab.
 *
 * @param ruleId - Rule ID
 * @param params - Pagination, name filter and scope
 * @param enabled - Whether to enable the query
 */
export const useTaggedDocuments = (
  ruleId: number,
  params?: {
    page?: number;
    pageSize?: number;
    nameContains?: string;
    tagScope?: "direct" | "subtree";
  },
  enabled: boolean = true,
): UseQueryResult<ApiResponse<PageResponse<RuleTaggedDocumentDto>>> => {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: queryKeys.rules.taggedDocuments(tenantId, ruleId, params),
    queryFn: () => ruleManagementApi.getTaggedDocuments(ruleId, params),
    enabled: enabled && ruleId > 0,
    staleTime: 2 * 60 * 1000,
  });
};

// ================================
// Export All
// ================================

export default {
  // Query Hooks
  useRules,
  useRule,
  useRuleCategories,
  useChildRules,
  useRuleHistory,
  useRuleStatistics,
  useSearchRules,

  // Mutation Hooks
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  useChangeRuleStatus,
  useDuplicateRule,
  useBulkUpdateStatus,
  useBulkDeleteRules,
  useValidateRule,

  // Utility Hooks
  usePrefetchRule,
  useInvalidateRules,

  // Tagged Documents
  useTaggedDocuments,

  // Query Keys
  ruleKeys,
};
